import { type VariantProps } from 'class-variance-authority';
import { Check, ChevronDown, X } from 'lucide-react';
import { forwardRef, useState, useRef, useEffect, HTMLAttributes } from 'react';
import { selectTriggerVariants } from '@/shared/constants/ui-variants';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant, ButtonSize } from '@/shared/types/button.types';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { TruncatedText } from './truncated-text';

export interface SelectOption {
  readonly value: string;
  readonly label: string;
  readonly disabled?: boolean;
}

interface BaseSelectProps
  extends
    Omit<HTMLAttributes<HTMLDivElement>, 'onChange'>,
    VariantProps<typeof selectTriggerVariants> {
  readonly options: readonly SelectOption[];
  readonly placeholder?: string;
  readonly disabled?: boolean;
  readonly error?: boolean;
  readonly clearable?: boolean;
}

interface SingleSelectProps extends BaseSelectProps {
  readonly multiple?: false | undefined;
  readonly value?: string | undefined;
  readonly onChange?: (value: string | undefined) => void;
}

interface MultipleSelectProps extends BaseSelectProps {
  readonly multiple: true;
  readonly value?: readonly string[] | undefined;
  readonly onChange?: (value: readonly string[]) => void;
}

export type SelectProps = SingleSelectProps | MultipleSelectProps;

/**
 * Helper functions for single selection mode
 */
const handleSingleSelect = (
  optionValue: string,
  onChange: ((value: string | undefined) => void) | undefined,
  setIsOpen: (open: boolean) => void
) => {
  onChange?.(optionValue);
  setIsOpen(false);
};

const handleSingleClear = (
  onChange: ((value: string | undefined) => void) | undefined
) => {
  onChange?.(undefined);
};

const isSingleSelected = (
  optionValue: string,
  value: string | undefined
): boolean => {
  return value === optionValue;
};

/**
 * Helper functions for multiple selection mode
 */
const handleMultipleSelect = (
  optionValue: string,
  value: readonly string[] | undefined,
  onChange: ((value: readonly string[]) => void) | undefined
) => {
  const currentValues = value ?? [];
  const newValues = currentValues.includes(optionValue)
    ? currentValues.filter((v) => v !== optionValue)
    : [...currentValues, optionValue];
  onChange?.(newValues);
};

const handleMultipleRemove = (
  optionValue: string,
  value: readonly string[] | undefined,
  onChange: ((value: readonly string[]) => void) | undefined
) => {
  const currentValues = value ?? [];
  const newValues = currentValues.filter((v) => v !== optionValue);
  onChange?.(newValues);
};

const handleMultipleClear = (
  onChange: ((value: readonly string[]) => void) | undefined
) => {
  onChange?.([] as const);
};

const isMultipleSelected = (
  optionValue: string,
  value: readonly string[] | undefined
): boolean => {
  return (value ?? []).includes(optionValue);
};

/**
 * Select Component
 *
 * A custom select component that supports both single and multiple selection modes.
 * Uses discriminated union types for type safety with helper functions for each mode.
 */
const Select = forwardRef<HTMLDivElement, SelectProps>(
  (
    {
      className,
      options,
      placeholder = 'Select...',
      disabled = false,
      error = false,
      clearable = true,
      value,
      onChange,
      multiple = false,
      size,
      variant,
      ...props
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target as Node)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () =>
          document.removeEventListener('mousedown', handleClickOutside);
      }

      return undefined;
    }, [isOpen]);

    const handleToggle = () => {
      if (!disabled) {
        setIsOpen(!isOpen);
      }
    };

    const handleSelect = (optionValue: string) => {
      if (multiple) {
        handleMultipleSelect(
          optionValue,
          value as readonly string[] | undefined,
          onChange as ((value: readonly string[]) => void) | undefined
        );
      } else {
        handleSingleSelect(
          optionValue,
          onChange as ((value: string | undefined) => void) | undefined,
          setIsOpen
        );
      }
    };

    const handleRemove = (optionValue: string, event: React.MouseEvent) => {
      event.stopPropagation();
      if (multiple) {
        handleMultipleRemove(
          optionValue,
          value as readonly string[] | undefined,
          onChange as ((value: readonly string[]) => void) | undefined
        );
      }
    };

    const handleClear = (event: React.MouseEvent) => {
      event.stopPropagation();
      if (multiple) {
        handleMultipleClear(
          onChange as ((value: readonly string[]) => void) | undefined
        );
      } else {
        handleSingleClear(
          onChange as ((value: string | undefined) => void) | undefined
        );
      }
    };

    const isSelected = (optionValue: string): boolean => {
      if (multiple) {
        return isMultipleSelected(
          optionValue,
          value as readonly string[] | undefined
        );
      }
      return isSingleSelected(optionValue, value as string | undefined);
    };

    const getSelectedOptions = (): readonly SelectOption[] => {
      if (multiple) {
        const selectedValues = (value as readonly string[] | undefined) ?? [];
        return options.filter((opt) => selectedValues.includes(opt.value));
      }
      return value ? options.filter((opt) => opt.value === value) : [];
    };

    const selectedOptions = getSelectedOptions();
    const hasValue = multiple
      ? selectedOptions.length > 0
      : value !== undefined;

    return (
      <div
        ref={containerRef}
        className={cn('relative w-full', className)}
        {...props}
      >
        <div
          ref={ref}
          role="combobox"
          aria-controls="select-listbox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
          className={cn(
            selectTriggerVariants({ size, variant }),
            disabled && 'cursor-not-allowed opacity-50',
            !disabled && 'cursor-pointer hover:border-accent',
            error && 'border-destructive focus-visible:ring-destructive',
            isOpen && 'ring-2 ring-ring ring-offset-2'
          )}
          onClick={handleToggle}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              handleToggle();
            } else if (e.key === 'Escape' && isOpen) {
              setIsOpen(false);
            }
          }}
        >
          <div className="flex flex-1 flex-wrap gap-1.5 overflow-hidden">
            {multiple && selectedOptions.length > 0
              ? selectedOptions.map((option, index) => (
                  <span
                    key={option.value}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-full bg-accent px-2.5 py-1 text-xs font-medium text-accent-foreground',
                      'max-w-full truncate',
                      // Hide overflow chips on small screens
                      index >= 2 && 'hidden sm:inline-flex'
                    )}
                  >
                    <TruncatedText>{option.label}</TruncatedText>
                    {clearable && (
                      <Button
                        type="button"
                        variant={ButtonVariant.Ghost}
                        size={ButtonSize.Small}
                        onClick={(e) => handleRemove(option.value, e)}
                        className="size-auto rounded-full p-0.5 hover:bg-accent/80"
                        aria-label={`Remove ${option.label}`}
                      >
                        <X className="size-3" aria-hidden="true" />
                      </Button>
                    )}
                  </span>
                ))
              : null}
            {/* Show count of hidden chips on mobile */}
            {multiple && selectedOptions.length > 2 && (
              <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-1 text-xs font-medium text-muted-foreground sm:hidden">
                +{selectedOptions.length - 2} more
              </span>
            )}
            {!multiple && selectedOptions.length > 0 ? (
              <TruncatedText className="text-foreground">
                {selectedOptions[0]?.label ?? ''}
              </TruncatedText>
            ) : null}
            {selectedOptions.length === 0 ? (
              <span className="text-muted-foreground">{placeholder}</span>
            ) : null}
          </div>
          <div className="flex items-center gap-1 pl-2">
            {hasValue && !disabled && clearable && (
              <Button
                type="button"
                variant={ButtonVariant.Ghost}
                size={ButtonSize.Small}
                onClick={handleClear}
                className="size-7 min-h-[28px] min-w-[28px] rounded-sm p-0"
                aria-label="Clear selection"
              >
                <X className="size-4" aria-hidden="true" />
              </Button>
            )}
            <ChevronDown
              className={cn(
                'h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
              aria-hidden="true"
            />
          </div>
        </div>

        {isOpen && (
          <div
            role="listbox"
            aria-multiselectable={multiple}
            className={cn(
              'absolute z-50 mt-1 w-full rounded-md border border-border bg-popover shadow-lg',
              'animate-in fade-in-0 zoom-in-95',
              'max-h-60 overflow-auto'
            )}
          >
            {options.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No options available
              </div>
            ) : (
              options.map((option) => {
                const selected = isSelected(option.value);

                // For multi-select, use div to avoid nested buttons (Checkbox is a button)
                if (multiple) {
                  return (
                    <div
                      key={option.value}
                      role="option"
                      aria-selected={selected}
                      aria-disabled={option.disabled}
                      data-selected={selected}
                      tabIndex={option.disabled ? -1 : 0}
                      className={cn(
                        'relative flex h-auto min-h-[44px] w-full cursor-pointer items-center justify-start gap-2 rounded-none px-4 py-3 text-left text-sm font-normal transition-all',
                        'hover:bg-accent focus:bg-accent focus:outline-none active:bg-accent/80 active:scale-[0.98]',
                        option.disabled && 'cursor-not-allowed opacity-50',
                        selected && 'bg-accent/50'
                      )}
                      onClick={() => {
                        if (!option.disabled) {
                          handleSelect(option.value);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (
                          (e.key === 'Enter' || e.key === ' ') &&
                          !option.disabled
                        ) {
                          e.preventDefault();
                          handleSelect(option.value);
                        }
                      }}
                    >
                      <Checkbox
                        checked={selected}
                        disabled={option.disabled}
                        className="pointer-events-none"
                        aria-hidden="true"
                        tabIndex={-1}
                      />
                      <span className="flex-1 text-foreground">
                        {option.label}
                      </span>
                    </div>
                  );
                }

                // For single-select, use Button (no nested button issue)
                return (
                  <Button
                    key={option.value}
                    type="button"
                    variant={ButtonVariant.Ghost}
                    role="option"
                    aria-selected={selected}
                    data-selected={selected}
                    disabled={option.disabled}
                    className={cn(
                      'relative flex h-auto min-h-[44px] w-full justify-start gap-2 rounded-none px-4 py-3 text-left text-sm font-normal transition-all',
                      'hover:bg-accent focus:bg-accent active:bg-accent/80 active:scale-[0.98]',
                      option.disabled && 'cursor-not-allowed opacity-50',
                      selected && 'bg-accent/50'
                    )}
                    onClick={() => {
                      if (!option.disabled) {
                        handleSelect(option.value);
                      }
                    }}
                  >
                    <span
                      className={cn(
                        'flex-1 text-foreground',
                        selected && 'font-medium'
                      )}
                    >
                      {option.label}
                    </span>
                    {selected && (
                      <Check
                        className="size-4 flex-shrink-0 text-foreground"
                        aria-hidden="true"
                      />
                    )}
                  </Button>
                );
              })
            )}

            {/* Done button for multi-select on mobile */}
            {multiple && (
              <div className="sticky bottom-0 border-t border-border bg-popover p-2 lg:hidden">
                <Button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="h-11 min-h-[44px] w-full"
                  aria-label={`Done selecting, ${selectedOptions.length} selected`}
                >
                  Done
                  {selectedOptions.length > 0 && ` (${selectedOptions.length})`}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';

export { Select };
export { SelectSize, SelectVariant } from '@/shared/types/select.types';
